// entitlements — small helpers for dashboard feature gating (PE-1624 / PE-1625).
//
// The gating MECHANISM (resolve once per billingDetails, expose can()/limit())
// lives in src/contexts/EntitlementsContext.tsx. This file is the thin support
// layer consumers share:
//   - FEATURE: typo-safe constants for the feature keys the dashboard gates on
//     (a subset of the engine catalog — only the surfaces we actually gate).
//   - unlockTierFor / unlockTierLabel: which tier unlocks a feature, DERIVED
//     from the engine grid (CATALOG) — never a hand-maintained parallel catalog,
//     so the "Available on {tier}" copy can't drift from the source of truth.
//
// Why not a per-surface "inventory" map: the unlock tier is derivable from the
// engine, and each surface applies its own can() check inline (the gating is
// proactive UX; the server is authority). A second catalog would only drift.

import { CATALOG, TIERS, TIER_RANK, type Tier } from '@charitystack/entitlements';

// Feature keys the dashboard gates on. These MUST match keys in the engine
// catalog (feature-gating-source.yaml); unlockTierFor returns null for any key
// not in the grid, which would surface as "a higher plan" rather than a crash.
export const FEATURE = {
  // Growth elements (Base+) — the /elements surface + the overlay display option.
  FUNDRAISING_BAR: 'fundraising_bar',
  QR_CODES: 'qr_codes',
  SUPPORTER_LISTS: 'supporter_lists',
  FUNDRAISING_FLAG: 'fundraising_flag',
  ADD_TO_CART: 'add_to_cart',
  OVERLAY_POPUP: 'overlay_popup',
  PREFILLED_LINKS: 'prefilled_links',
  // Contact Portal element (Base+) — the embeddable donor-portal widget, created
  // from the Elements surface. Distinct from the STARTER+ standalone hosted portal
  // (the engine's `donor_portal` key), which is NOT gated here.
  CONTACT_PORTAL: 'contact_portal_element',
  // Contacts (Base+).
  CONTACT_MERGING: 'contact_merging',
  EOY_BULK_TAX_RECEIPTS: 'eoy_bulk_tax_receipts',
  // Marketing (Base+).
  // BROADCASTS = the email/marketing-SECTION gate (renamed from broadcasts_email_sms
  // in the SMS split). It does NOT gate SMS — SMS is the separate BROADCASTS_SMS
  // boolean below. All existing MARKETING_FEATURE_KEYS / section gates use this key.
  BROADCASTS: 'broadcasts_email',
  // BROADCASTS_SMS = the "SMS is on" gate (Plus+), DECOUPLED from the
  // sms_included_per_month credit allotment. Resolve this via can()/isEntitled — do
  // NOT gate SMS on `limit('sms_included_per_month') > 0` anymore (that limit is now
  // covered-credits only).
  BROADCASTS_SMS: 'broadcasts_sms',
  SEGMENTS: 'segments',
  EMAIL_BUILDER: 'email_builder',
  EMAIL_TEMPLATES: 'email_templates',
  MARKETING_AUTOMATION: 'marketing_automation_journeys',
  CUSTOM_EMAIL_DOMAIN: 'custom_email_domain',
  // Integrations (Plus+).
  CRM_INTEGRATIONS: 'crm_integrations',
} as const;

// The element types surfaced under /elements (all Base+ growth elements). The
// Elements page/route is accessible if the org can use ANY of them — gating the
// route on a single key would break if product ever splits the tiers.
export const ELEMENT_FEATURE_KEYS: readonly string[] = [
  FEATURE.FUNDRAISING_BAR,
  FEATURE.QR_CODES,
  FEATURE.SUPPORTER_LISTS,
  FEATURE.FUNDRAISING_FLAG,
  FEATURE.ADD_TO_CART,
  FEATURE.OVERLAY_POPUP,
  FEATURE.PREFILLED_LINKS,
  FEATURE.CONTACT_PORTAL,
];

// The PocketFlows Marketing surface (Broadcasts / Segments / Email Templates) —
// all Base+ features. The section/routes are accessible if the org can use ANY
// of them; gating on a single key would break if product splits the tiers.
//
// IMPORTANT — marketing fails CLOSED, unlike the Elements surface. Marketing is
// the ONE feature excluded from the legacy-safe default bundle (engine §11), so
// the conservative posture when entitlement resolution is degraded is OFF, not
// ON. A STARTER org (or a degraded/unknown one) must never reach marketing — see
// the `failClosed` gating in Sidebar.js and the marketing routes in App.js.
export const MARKETING_FEATURE_KEYS: readonly string[] = [
  FEATURE.BROADCASTS,
  FEATURE.SEGMENTS,
  FEATURE.EMAIL_BUILDER,
  FEATURE.EMAIL_TEMPLATES,
];

const TIER_LABEL: Record<Tier, string> = {
  starter: 'Starter',
  base: 'Base',
  plus: 'Plus',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

// The lowest tier that unlocks a feature, read from the engine boolean grid.
// Returns null when the feature is on at NO tier (roadmap/unknown) — callers
// treat that as "not currently sellable" and fall back to a generic label.
export function unlockTierFor(feature: string): Tier | null {
  const row = CATALOG.features[feature];
  if (!row) return null;
  for (const tier of TIERS) {
    if (row[tier]) return tier;
  }
  return null;
}

// Display label for the unlocking tier, e.g. "Base". Falls back to "a higher
// plan" for roadmap/unknown features so the affordance never renders "null".
export function unlockTierLabel(feature: string): string {
  const tier = unlockTierFor(feature);
  return tier ? TIER_LABEL[tier] : 'a higher plan';
}

// The cheapest tier ABOVE `currentTier` that RAISES a usage LIMIT — read from
// the engine LIMITS grid (CATALOG.limits), not the feature grid. This is the
// correct lookup for a "you're at your cap, upgrade to raise it" CTA: the
// feature grid only says which tier first ENABLES a feature (Base enables
// broadcasts), which would wrongly tell a capped Base org to "upgrade to Base".
//
// "Raises" = strictly higher number, or 'unlimited'. Mirrors the backend's
// PE-1623 unlockTierFor so FE and server name the same upgrade. Returns null if
// no higher tier lifts the cap (already on the top, or unknown key).
export function unlockLimitTierFor(
  limitKey: string,
  currentTier: Tier,
): Tier | null {
  const row = CATALOG.limits[limitKey];
  if (!row) return null;
  const currentVal = row[currentTier];
  if (currentVal === 'unlimited') return null; // nothing higher to lift it to
  const higher = TIERS.filter((t) => TIER_RANK[t] > TIER_RANK[currentTier]).sort(
    (a, b) => TIER_RANK[a] - TIER_RANK[b],
  );
  for (const tier of higher) {
    const v = row[tier];
    if (v === 'unlimited') return tier;
    if (typeof v === 'number' && (typeof currentVal !== 'number' || v > currentVal)) {
      return tier;
    }
  }
  return null;
}

// Display label for the limit-unlocking tier, e.g. "Plus". Falls back to "a
// higher plan" so the CTA never renders "null".
export function unlockLimitTierLabel(limitKey: string, currentTier: Tier): string {
  const tier = unlockLimitTierFor(limitKey, currentTier);
  return tier ? TIER_LABEL[tier] : 'a higher plan';
}
